import './party.css';
import './aerie.css';

import { type CharacterPortrait, getZone, type PartyState } from '@shefira/shared';

import { type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import { iconButton } from './hud.js';
import { partyOrder } from './party-order.js';
import { headPortrait } from './portrait.js';
import { uiFormat, uiText } from './text.js';

export interface PartyFramesOptions {
  onLeave: () => void;
  onKick: (characterId: string) => void;
  /** A leader ends the alliance with the other party (ADR 0068 §5). */
  onLeaveAlliance: () => void;
}

/**
 * The party's frames (ADR 0032), on the left edge between the minimap and
 * the chat: each member's head, the player's own first, then name, level and
 * health, and where they are when not in this zone. Only a view of what the
 * server sent in `party`.
 */
export class PartyFrames {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  /** The allied party, one line under the members (ADR 0068 §5). */
  private readonly alliance: HTMLElement;
  private readonly allianceText: HTMLElement;
  private readonly allianceLeave: HTMLButtonElement;
  private atlas: DomAtlas | null = null;
  /** Each member's head, redrawn only when their outfit changes. */
  private readonly portraits = new Map<string, { key: string; canvas: HTMLCanvasElement }>();
  private last?: { state: PartyState; selfId: string; zoneId: string };

  constructor(private readonly options: PartyFramesOptions) {
    this.root = document.createElement('section');
    this.root.className = 'party';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Groupe'));
    const header = document.createElement('div');
    header.className = 'party__header';
    const title = document.createElement('span');
    title.textContent = uiText('Groupe');
    const leave = iconButton('close', 'Quitter le groupe');
    leave.classList.add('party__leave');
    leave.addEventListener('click', () => {
      this.options.onLeave();
    });
    header.append(title, leave);
    this.list = document.createElement('ol');
    this.list.className = 'party__list';
    this.alliance = document.createElement('div');
    this.alliance.className = 'alliance';
    this.alliance.hidden = true;
    this.allianceText = document.createElement('span');
    this.allianceText.className = 'alliance__text';
    this.allianceLeave = iconButton('close', 'Rompre l’alliance');
    this.allianceLeave.classList.add('party__kick');
    this.allianceLeave.addEventListener('click', () => {
      this.options.onLeaveAlliance();
    });
    this.alliance.append(this.allianceText, this.allianceLeave);
    this.root.append(header, this.list, this.alliance);
    document.body.append(this.root);
    void loadCharacterAtlas().then((atlas) => {
      this.atlas = atlas;
      if (this.last !== undefined) this.show(this.last.state, this.last.selfId, this.last.zoneId);
    });
  }

  private portraitOf(id: string, portrait: CharacterPortrait): HTMLCanvasElement | undefined {
    if (this.atlas === null) return undefined;
    const key = JSON.stringify(portrait);
    const cached = this.portraits.get(id);
    if (cached?.key === key) return cached.canvas;
    const canvas = headPortrait(this.atlas, portrait);
    canvas.className = 'party__portrait';
    this.portraits.set(id, { key, canvas });
    return canvas;
  }

  show(state: PartyState, selfId: string, zoneId: string): void {
    this.last = { state, selfId, zoneId };
    this.root.hidden = state.members.length === 0;
    for (const id of this.portraits.keys()) {
      if (!state.members.some((member) => member.id === id)) this.portraits.delete(id);
    }
    this.list.replaceChildren();
    const leading = state.leaderId === selfId;
    for (const member of partyOrder(state.members, selfId)) {
      const item = document.createElement('li');
      item.className = 'party__member';
      const self = member.id === selfId;
      if (self) item.classList.add('party__member--self');
      if (!member.online) item.classList.add('party__member--offline');
      else if (member.away) item.classList.add('party__member--away');

      const name = document.createElement('div');
      name.className = 'party__name';
      const who = document.createElement('span');
      who.className = 'party__who';
      who.textContent = uiText(`${member.id === state.leaderId ? '* ' : ''}${member.name}`);
      who.title = uiText(member.name);
      const level = document.createElement('span');
      level.className = 'party__level';
      level.textContent = uiFormat('niv. {level}', { level: member.level });
      name.append(who, level);

      const track = document.createElement('div');
      track.className = 'party__track';
      const fill = document.createElement('div');
      fill.className = 'party__fill';
      fill.style.width = `${String(Math.round((100 * member.hp) / member.maxHp))}%`;
      track.append(fill);
      track.title = `${String(member.hp)} / ${String(member.maxHp)}`;

      const portrait = this.portraitOf(member.id, {
        classId: member.classId,
        look: member.look,
        appearance: member.appearance,
      });
      // The column stays even before the atlas arrives: an empty slot holds it.
      item.append(
        portrait ?? Object.assign(document.createElement('div'), { className: 'party__portrait' }),
      );
      const body = document.createElement('div');
      body.className = 'party__body';
      body.append(name, track);
      item.append(body);
      const elsewhere =
        member.zoneId !== zoneId ? (getZone(member.zoneId)?.name ?? 'Ailleurs') : undefined;
      const where = !member.online
        ? uiText('Hors ligne')
        : member.away
          ? // Away from the keyboard (ADR 0080), and where if not here.
            elsewhere === undefined
            ? uiText('Absent')
            : uiFormat('Absent · {place}', { place: uiText(elsewhere) })
          : elsewhere === undefined
            ? undefined
            : uiText(elsewhere);
      if (where !== undefined) {
        const place = document.createElement('div');
        place.className = 'party__place';
        place.textContent = where;
        body.append(place);
      }
      if (leading && !self) {
        const kick = iconButton('close', uiFormat('Exclure {name}', { name: member.name }));
        kick.classList.add('party__kick');
        kick.addEventListener('click', () => {
          this.options.onKick(member.id);
        });
        item.append(kick);
      }
      this.list.append(item);
    }
  }

  /** The allied party, or null for none; `canLeave` for either leader. */
  showAlliance(ally: { leader: string; count: number } | null, canLeave: boolean): void {
    this.alliance.hidden = ally === null;
    if (ally === null) return;
    this.allianceText.textContent = uiFormat('Alliance : groupe de {leader} ({count})', {
      leader: ally.leader,
      count: ally.count,
    });
    this.allianceText.title = this.allianceText.textContent;
    this.allianceLeave.hidden = !canLeave;
  }

  destroy(): void {
    this.root.remove();
  }
}
